import { useCallback, useMemo, useState } from 'react'
import type { JSX } from 'react'
import { Link, useLocation } from 'react-router-dom'

import {
  IconTile,
  Sidebar,
  useSidebar,
  VisuallyHidden,
} from '@buffer-mono/popcorn'

import { useChannelsConnected } from '~publish/contexts/AccountContext'
import {
  buildUrlFromSavedView,
  captureFilters,
  deriveSectionFromRoute,
  deriveViewFromRoute,
  findMatchingSavedView,
} from '~publish/helpers/savedViews'
import { useSavedViewTracking } from '~publish/hooks/useSavedViewTracking'

import { DeleteViewAlertDialog } from './DeleteViewAlertDialog'
import type { SavedView } from './hooks'
import { RenameViewDialog } from './RenameViewDialog'
import { savedViewIdentity } from './savedViewIdentity'
import { SavedViewItemMenu } from './SavedViewItemMenu'

import styles from './SavedViewItem.module.css'

type SavedViewItemProps = {
  view: SavedView
}

/**
 * Single saved view row: link to the view URL with a trailing actions menu.
 * Returns the row content as a fragment so the parent can decide between a
 * plain `<li>` and a sortable wrapper without nesting list items.
 */
export function SavedViewItem({ view }: SavedViewItemProps): JSX.Element {
  const { pathname, search } = useLocation()
  const { state } = useSidebar()
  const isCollapsed = state === 'collapsed'
  // Resolve the persisted (nullable, unconstrained) icon/color into a valid
  // glyph + tint, falling back to the default bookmark identity for legacy
  // views that predate the identity fields.
  const identity = savedViewIdentity(view)
  // A view pointing at a removed/disconnected channel must fall back to the
  // section root rather than dead-ending on a stale channel route, so the URL
  // builder needs the set of channels that still exist.
  const channels = useChannelsConnected()
  const validChannelIds = useMemo(
    () => channels.map((channel) => channel.id),
    [channels],
  )
  const url = buildUrlFromSavedView(view, validChannelIds)

  // Use the same matching rules as `SaveViewButton` so the sidebar row and
  // the page-header star always agree on "which saved view is active".
  // NavLink's default `aria-current` compares pathnames only, which paints
  // every row sharing a pathname as selected when filter query-params are
  // applied — this deep-equals the captured filter payload so exactly one
  // row (the truly matching view) is announced as current.
  const isActive = useMemo(() => {
    const searchParams = new URLSearchParams(search)
    const section = deriveSectionFromRoute(pathname)
    const currentView = deriveViewFromRoute(pathname, searchParams)
    const capture = captureFilters(section, pathname, searchParams)
    // An invalid URL can never match a stored saved view: stored views all
    // pass the same schema on creation, so a failure here means the user
    // navigated to a URL with filters that no canonical document represents.
    if (!capture.ok) return false
    const match = findMatchingSavedView(
      [view],
      section,
      currentView,
      capture.filters,
    )
    return Boolean(match)
  }, [pathname, search, view])

  // Dialog state lives here (not in SavedViewItemMenu) so the dialogs render
  // as React siblings of the `Sidebar.Button as={Link}` subtree. Synthetic
  // clicks inside the dialog (e.g. Save) must not bubble through the Link's
  // onClick (which calls `preventDefault()`) and cancel the implicit form
  // submit. The menu trigger is also a sibling of the Link (not a child)
  // because Popcorn's `Sidebar.Button` renders `suffix` inside its root,
  // which would nest a <button> inside an <a>.
  const [renameOpen, setRenameOpen] = useState(false)
  const [deleteOpen, setDeleteOpen] = useState(false)

  const { trackLoaded } = useSavedViewTracking()
  // Fire on click rather than on navigation completion: the user has
  // intentionally activated the view either way, and capturing here avoids
  // missing the event when navigation is intercepted (modifier-clicks,
  // route guards). The hook is responsible for the feature-flag gate.
  const handleClick = useCallback((): void => {
    trackLoaded(
      { id: view.id, section: view.section, view: view.view },
      'sidebar',
    )
  }, [trackLoaded, view.id, view.section, view.view])

  return (
    <>
      <Sidebar.Button
        as={Link}
        to={url}
        onClick={handleClick}
        selected={isActive}
        aria-current={isActive ? 'page' : undefined}
        className={styles.button}
        prefix={
          <IconTile
            icon={identity.icon}
            color={identity.color}
            appearance={isCollapsed ? 'tile' : 'plain'}
          />
        }
      >
        {isCollapsed ? (
          // The prefix carries the visible glyph in the collapsed rail
          // (Sidebar hides item content when a prefix is present), so the
          // content is only the full name for screen readers.
          <VisuallyHidden translate="no">{view.name}</VisuallyHidden>
        ) : (
          <span className={styles.name} translate="no">
            {view.name}
          </span>
        )}
      </Sidebar.Button>
      {!isCollapsed && (
        <div className={styles.actions}>
          <SavedViewItemMenu
            className={styles.menu}
            viewName={view.name}
            viewId={view.id}
            onRename={(): void => setRenameOpen(true)}
            onDelete={(): void => setDeleteOpen(true)}
          />
        </div>
      )}
      <RenameViewDialog
        view={view}
        open={renameOpen}
        onOpenChange={setRenameOpen}
      />
      <DeleteViewAlertDialog
        view={view}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
      />
    </>
  )
}

export { styles as savedViewItemStyles }
